/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from "@emotion/react";
import { FunctionComponent } from "react";

import { StrapiCareersPage } from "@/types/strapi";
import { ColumnStandardCount } from "@/types/tokens/grid";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import { AllIcons } from "@/tokens/configs/icon_config";
import { Spacing } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Icon } from "@/ui/atoms/icon";
import { SubgridContainer } from "@/ui/atoms/subgrid_container";
import { Text } from "@/ui/atoms/text";

import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";
import { convertToRem } from "@/util/ui_util";

interface CareersBenefitsProps extends StrapiCareersPage {
    className?: SerializedStyles;
}

export const CareersBenefits: FunctionComponent<CareersBenefitsProps> = (
    props,
) => {
    /**
     * Theme
     */
    const theme = useTypedTheme();

    /**
     * Styles
     */
    const iconStyles = css(
        buildStylesByBreakpoint("marginBottom", {
            extraSmall: Spacing["spacing-5"],
            large: Spacing["spacing-6"],
        }),
        {
            alignItems: "center",
            background: Colors[theme.backgrounds.backgroundSecondary],
            borderRadius: BorderRadiuses.borderMedium,
            display: "flex",
            height: convertToRem(64),
            justifyContent: "center",
            width: convertToRem(64),
        },
    );

    const benefitItemStyles = css(
        buildStylesByBreakpoint("borderBottom", {
            extraSmall: `1px solid ${Colors["lighten-20"]}`,
            medium: "none",
        }),
        buildStylesByBreakpoint("borderLeft", {
            medium: `1px solid ${Colors["lighten-20"]}`,
        }),
        buildStylesByBreakpoint("padding", {
            extraSmall: `${Spacing["spacing-8"]} 0`,
            medium: Spacing["spacing-5"],
            large: Spacing["spacing-6"],
        }),
    );

    /**
     * Rendering
     */
    return (
        <GridContainer
            className={props.className}
            marginBottom="CareersContainerMargin"
            marginTop="CareersContainerMargin"
        >
            <Text
                columnSpan={{ extraSmall: "full", medium: 8 }}
                columnStart={{ extraSmall: 1, medium: 3 }}
                fontSize="CareerHeadline"
                tag="h2"
                textAlign="center"
            >
                {props.Benefits_Headline}
            </Text>

            <SubgridContainer
                className={css({
                    gridRowGap: Spacing["spacing-8"],
                })}
                marginTop="CareersBenefitsItemsContainer"
                tag="ul"
            >
                {props.Benefit_Items.map((_benefit, _index) => (
                    <GridColumn
                        className={benefitItemStyles}
                        columnSpan={{ extraSmall: "full", medium: 4 }}
                        columnStart={{
                            extraSmall: 1,
                            medium: (1 +
                                (_index % 3) * 4) as ColumnStandardCount,
                        }}
                        key={`careers-benefits::benefit::${_benefit.id}`}
                        tag="li"
                    >
                        <Icon
                            className={iconStyles}
                            color="green-20"
                            slug={_benefit.Icon as AllIcons}
                        />

                        <Text tag="h3">{_benefit.Headline}</Text>
                    </GridColumn>
                ))}
            </SubgridContainer>
        </GridContainer>
    );
};
